import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar, Clock, Video, RefreshCw, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const STATUS_CONFIG = {
  'pending_scheduling': {
    label: 'Pending Scheduling',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Clock,
    description: 'Please select a time slot to complete your booking.'
  },
  'scheduled_pending_zoom': {
    label: 'Scheduled - Awaiting Zoom',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Video,
    description: 'Your consultation is scheduled. Zoom link will be sent soon.'
  },
  'zoom_sent': {
    label: 'Ready',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle,
    description: 'Your consultation is confirmed with Zoom link.'
  },
  'cancelled': {
    label: 'Cancelled',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: X,
    description: 'This consultation has been cancelled.'
  },
  'late_cancelled': {
    label: 'Late Cancelled',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: AlertCircle,
    description: 'This consultation was cancelled with less than 24 hours notice.'
  }
};

export function CustomerBookingView({ 
  booking, 
  loading, 
  onReschedule, 
  onCancel,
  showActions = true 
}) {
  if (loading) {
    return (
      <Card data-testid="booking-view-loading">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          <span className="ml-3 text-gray-500">Loading your booking...</span>
        </CardContent>
      </Card>
    );
  }

  if (!booking) {
    return (
      <Alert variant="destructive" data-testid="booking-not-found">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Booking not found or access link has expired.
        </AlertDescription>
      </Alert>
    );
  }

  const statusConfig = STATUS_CONFIG[booking.status] || STATUS_CONFIG['pending_scheduling'];
  const StatusIcon = statusConfig.icon;
  const isCancelled = ['cancelled', 'late_cancelled'].includes(booking.status);
  const canModify = !isCancelled && booking.can_reschedule;

  // Format date nicely
  const formattedDate = booking.scheduled_date 
    ? format(parseISO(booking.scheduled_date), 'EEEE, MMMM d, yyyy')
    : null;

  return (
    <Card className="overflow-hidden" data-testid="customer-booking-view">
      {/* Status Header */}
      <div className={`px-6 py-4 ${statusConfig.color.replace('text-', 'bg-').split(' ')[0]}`}>
        <div className="flex items-center gap-3">
          <StatusIcon className="w-6 h-6" />
          <div>
            <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
            <p className="text-sm mt-1 opacity-80">{statusConfig.description}</p>
          </div>
        </div>
      </div>

      <CardContent className="p-6">
        {/* Customer Info */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Hi {booking.first_name || 'there'}! 👋
          </h3>
          <p className="text-gray-600">{booking.email}</p>
        </div>

        {/* Scheduled Time */}
        {booking.scheduled_date && booking.scheduled_time && (
          <div className={`p-4 rounded-lg mb-6 ${isCancelled ? 'bg-gray-50' : 'bg-blue-50'}`}>
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                isCancelled ? 'bg-gray-200' : 'bg-blue-100'
              }`}>
                <Calendar className={`w-6 h-6 ${isCancelled ? 'text-gray-500' : 'text-blue-600'}`} />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Scheduled For</p>
                <p className={`text-xl font-semibold ${isCancelled ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                  {formattedDate}
                </p>
                <p className={`text-lg ${isCancelled ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                  {booking.scheduled_time} ET (Eastern Time)
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Zoom Link (if available) */}
        {booking.has_zoom_link && booking.zoom_join_url && !isCancelled && (
          <div className="p-4 bg-green-50 rounded-lg mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Video className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-2">Zoom Meeting</p>
                <a 
                  href={booking.zoom_join_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
                  data-testid="join-zoom-btn"
                >
                  Join Zoom Meeting
                </a>
                {booking.zoom_password && (
                  <p className="text-sm text-gray-600 mt-2">
                    Password: <code className="bg-gray-100 px-2 py-0.5 rounded">{booking.zoom_password}</code>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Waiting for Zoom */}
        {booking.status === 'scheduled_pending_zoom' && !booking.has_zoom_link && (
          <Alert className="mb-6 border-blue-200 bg-blue-50">
            <Video className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Zoom link coming soon!</strong> You'll receive an email with your Zoom meeting link before your appointment.
            </AlertDescription>
          </Alert>
        )}

        {/* Reschedule Count */}
        {!isCancelled && booking.reschedule_count > 0 && (
          <p className="text-sm text-gray-500 mb-4">
            Rescheduled {booking.reschedule_count} time(s). 
            {booking.can_reschedule 
              ? `${booking.max_reschedules - booking.reschedule_count} reschedule(s) remaining.`
              : 'No more reschedules available.'
            }
          </p>
        )}

        {/* Action Buttons */}
        {showActions && !isCancelled && (
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            {canModify && (
              <Button
                onClick={onReschedule}
                variant="outline"
                className="flex-1"
                data-testid="reschedule-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reschedule
              </Button>
            )}
            <Button
              onClick={onCancel}
              variant="outline"
              className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
              data-testid="cancel-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel Consultation
            </Button>
          </div>
        )}

        {/* Cancelled Message */}
        {isCancelled && (
          <div className="pt-4 border-t text-center">
            <p className="text-gray-600 mb-4">Would you like to book a new consultation?</p>
            <Button
              onClick={() => window.location.href = '/book-consultation'}
              className="bg-gradient-to-r from-[#D4AF37] to-[#B99610] text-black"
              data-testid="book-new-btn"
            >
              Book New Consultation
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CustomerBookingView;
